import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, User, ExternalLink, BookOpen, Star, Share2, Eye, Download, Bookmark, Github, Play, AlertCircle, Loader2, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Footer2 } from '../components/Footer2';
import { supabase } from '../lib/supabase';

// Simple Button component since we're not using shadcn
const Button = ({ variant = "default", size = "default", children, className = "", onClick, ...props }) => {
  const baseClasses = "inline-flex items-center justify-center font-medium transition-all duration-200";
  
  const sizeClasses = {
    default: "px-4 py-2",
    sm: "px-3 py-1 text-sm"
  };
  
  const variantClasses = variant === "outline" 
    ? "border-2 border-black bg-white text-black hover:bg-gray-50 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]"
    : "bg-[#0395fc] text-white border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]";
  
  const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses} ${className}`;

  return (
    <button className={combinedClasses} onClick={onClick} {...props}>
      {children}
    </button>
  );
};

const ArticleTemplate = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true, threshold: 0.1 });
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shares, setShares] = useState(0);
  const [hasShared, setHasShared] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Fetch article from database
  const fetchArticle = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: articleData, error: fetchError } = await supabase
        .from('articles')
        .select(`
          id,
          title,
          excerpt,
          content,
          slug,
          article_type,
          status,
          published_at,
          featured_image_url,
          tags,
          author_id,
          reading_time,
          external_url,
          category,
          view_count,
          like_count
        `)
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No rows returned
          setError('Article not found');
        } else {
          throw fetchError;
        }
        return;
      }

      if (articleData) {
        // Format the article data for display
        const formattedArticle = {
          id: articleData.id,
          title: articleData.title,
          subtitle: articleData.excerpt || '',
          author: 'Anand Lo', // Simplified for now
          authorInfo: '',
          publishDate: new Date(articleData.published_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          readTime: articleData.reading_time ? `${articleData.reading_time} min read` : '15 min read',
          difficulty: 'Advanced', // Could be added to database
          category: articleData.category || 'Research Paper',
          tags: articleData.tags || [],
          summary: articleData.excerpt || articleData.content?.substring(0, 300) + '...' || 'No summary available.',
          content: articleData.content ? [{
            type: "section",
            title: "Article Content",
            content: articleData.content
          }] : [],
          keyTakeaways: [], // Could be added to database
          originalPaper: articleData.external_url || '#',
          additionalResources: [], // Could be added to database
          stats: {
            views: articleData.view_count || '65',
            likes: articleData.like_count || '8',
            downloads: '3',
            citations: '2'
          },
          weekNumber: 1, // This would need to be calculated based on article order
          year: new Date(articleData.published_at).getFullYear(),
          featured_image_url: articleData.featured_image_url,
          slug: articleData.slug
        };

        setArticle(formattedArticle);
      }
    } catch (err) {
      console.error('Error fetching article:', err);
      setError('Failed to load article. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (slug) {
      fetchArticle();
    }
  }, [slug, fetchArticle]);

  // Initialize shares from localStorage and article data
  useEffect(() => {
    if (article) {
      setShares(article.share_count || 0);
      
      // Check if user has already shared this article
      const sharedArticles = JSON.parse(localStorage.getItem('sharedArticles') || '[]');
      
      setHasShared(sharedArticles.includes(article.id));
    }
  }, [article]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareMenu && !event.target.closest('.share-menu-container')) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareMenu]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#0395fc] mx-auto mb-4" />
          <p className="text-gray-600 font-[var(--font-dm-sans)]">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-4xl font-black mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-8">{error || "The article you're looking for doesn't exist."}</p>
          <Link 
            to="/article-of-the-week"
            className="bg-[#0395fc] text-white px-6 py-3 font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200"
          >
            Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: article?.title,
          text: article?.subtitle || article?.summary,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('Article link copied to clipboard!');
      }
      
      if (!hasShared) {
        setShares(prev => prev + 1);
        setHasShared(true);
        // Save to localStorage
        const sharedArticles = JSON.parse(localStorage.getItem('sharedArticles') || '[]');
        if (!sharedArticles.includes(article?.id)) {
          sharedArticles.push(article.id);
          localStorage.setItem('sharedArticles', JSON.stringify(sharedArticles));
        }
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback to clipboard if sharing fails
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Article link copied to clipboard!');
        if (!hasShared) {
          setShares(prev => prev + 1);
          setHasShared(true);
        }
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
        alert('Could not share or copy the article link. Please try again.');
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Could not copy link. Please try again.');
    }
  };

  const handleShareToSocial = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(article.title);
    const description = encodeURIComponent(article.subtitle || article.summary || '');
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${title}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${title}&body=${description}%0A%0A${url}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section 
        ref={heroRef} 
        className="pt-10 md:pt-24 pb-6 md:pb-10 bg-cover bg-center bg-no-repeat relative overflow-hidden" 
        style={{ backgroundImage: `url(${article.featured_image_url})` }}
      >
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/50 z-0"></div>
        
        {/* Article Header Overlay */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-2 drop-shadow-lg">
            {article.title}
          </h2>
        </div>
        
        {/* Back Button */}
        <div className="container mx-auto px-4 relative z-10">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => navigate('/article-of-the-week')}
            className="flex items-center gap-2 text-white hover:text-yellow-300 transition-colors mb-8 font-bold"
          >
            <ArrowLeft className="size-5" />
            Back to Articles
          </motion.button>
        </div>

        {/* Animated background elements */}
        <motion.div 
          className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-2xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-white">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6 }}
              className="flex flex-wrap items-center gap-4 mb-8"
            >
              <span className="bg-white/20 text-white px-4 py-2 rounded-full border border-white/30 text-sm font-bold backdrop-blur-sm">
                {article.category}
              </span>
              <span className="bg-yellow-300 text-black px-4 py-2 rounded-full text-sm font-bold">
                {article.difficulty}
              </span>
              <span className="bg-white/20 text-white px-4 py-2 rounded-full border border-white/30 text-sm font-bold backdrop-blur-sm">
                Week {article.weekNumber}, {article.year}
              </span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl lg:text-5xl font-black mb-4 leading-tight"
            >
              {article.title}
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-white/90 mb-6"
            >
              {article.subtitle}
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-wrap items-center gap-6 text-white/80 mb-8"
            >
              <div className="flex items-center gap-2">
                <User className="size-4" />
                <span>{article.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="size-4" />
                <span>{article.publishDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="size-4" />
                <span>{article.readTime}</span>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-wrap items-center gap-4"
            >
              <motion.a
                href={article.originalPaper}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-[#0395fc] px-6 py-3 font-bold border-2 border-white shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200 flex items-center gap-2"
              >
                <ExternalLink className="size-4" />
                Read Original Paper
              </motion.a>
              
              <motion.button
                onClick={handleBookmark}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`p-3 rounded border-2 border-white/30 transition-colors ${
                  isBookmarked ? 'bg-yellow-300 text-black' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <Bookmark className="size-5" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 rounded bg-white/20 border-2 border-white/30 hover:bg-white/30 transition-colors text-white"
              >
                <Share2 className="size-5" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Like and Share Actions */}
            

            {/* Summary */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <h2 className="text-3xl font-black mb-6">Summary</h2>
              <div className="bg-[#0395fc]/5 border-l-4 border-[#0395fc] p-8">
                <div 
                  className="text-lg text-muted-foreground leading-relaxed prose prose-lg max-w-none text-left"
                  dangerouslySetInnerHTML={{ __html: article.summary }}
                />
              </div>
            </motion.div>

            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <h2 className="text-3xl font-black mb-8">Deep Dive</h2>
              <div className="space-y-8">
                {article.content.map((section, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white border-2 border-gray-200 p-6 hover:border-[#0395fc] transition-colors"
                  >
                    <h3 className="text-xl font-black mb-4 text-[#0395fc]">{section.title}</h3>
                    <div 
                      className="text-muted-foreground leading-relaxed prose prose-lg max-w-none text-left"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-50 border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 mb-8"
            >
              <div className="flex justify-center">
                <div className="relative flex-1 max-w-xs share-menu-container">
                  <Button 
                    variant="outline"
                    size="sm"
                    className="w-full border-2 border-black px-4 py-2 md:px-8 md:py-4"
                    onClick={() => setShowShareMenu(!showShareMenu)}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share 
                  </Button>
                  
                  {/* Share Menu Dropdown */}
                  {showShareMenu && (
                    <div className="absolute top-full mt-2 right-0 w-48 bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] z-50">
                      <button
                        onClick={() => handleShareToSocial('twitter')}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        Share on X
                      </button>
                      <button
                        onClick={() => handleShareToSocial('facebook')}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        Share on Facebook
                      </button>
                      <button
                        onClick={() => handleShareToSocial('linkedin')}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        Share on LinkedIn
                      </button>
                      <button
                        onClick={() => handleShareToSocial('email')}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                      >
                        <Mail className="h-4 w-4" />
                        Share via Email
                      </button>
                      <button
                        onClick={handleCopyLink}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium border-t border-gray-200"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {copySuccess ? '✓ Copied!' : 'Copy Link'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Tags */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-black mb-4">Tags</h2>
              <div className="flex flex-wrap gap-3">
                {article.tags.map((tag, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-[#0395fc] text-white px-4 py-2 font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200"
                  >
                    #{tag}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            
          </div>
        </div>
      </section>

      {/* Back to Articles CTA */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-black mb-4">Explore More Articles</h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Discover more groundbreaking research and insights in our curated collection of articles.
            </p>
            <motion.div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/article-of-the-week"
                className="bg-[#0395fc] hover:bg-[#0395fc]/90 text-white px-8 py-4 text-lg font-bold border-4 border-[#0395fc] shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] transition-all duration-200 inline-block"
              >
                View All Articles
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer2 />
    </div>
  );
};

export default ArticleTemplate;